import { Link } from 'react-router-dom';
import {
  LEP_EG_ID,
  COMMAND_CENTER_METRICS,
  COMMERCIAL_OPS,
  LIFECYCLE_STAGES,
  REVENUE_METRICS,
  CUSTOMER_SUCCESS_OPS,
  PRODUCT_OPS,
  RELEASE_OPS,
  MARKETPLACE_OPS,
  AI_OPS,
  SUPPORT_CHANNELS,
  PARTNER_TYPES,
  EXTENSION_TYPES,
  ROADMAP_CAPABILITIES,
  INNOVATION_CAPABILITIES,
  GA_ANALYTICS,
  GOVERNANCE_AREAS,
  ACCESSIBILITY_STANDARDS,
  ARCHITECTURE_STAGES,
  LIVE_ROUTES,
  DEMO_KPIS,
  DEMO_COMMERCIAL,
  DEMO_LIFECYCLE,
  DEMO_REVENUE,
  DEMO_CUSTOMER_SUCCESS,
  DEMO_PRODUCT_RELEASE,
  DEMO_MARKETPLACE_AI,
  DEMO_INNOVATION,
  buildGADashboard,
} from '../../enterprisega/framework';
import {
  GACommandCenterPanel,
  CommercialOperationsPanel,
  CustomerLifecyclePanel,
  RevenueOperationsPanel,
  CustomerSuccessOpsPanel,
  ProductReleaseOpsPanel,
  MarketplaceAIOpsPanel,
  InnovationPlatformPanel,
} from '../../components/enterprisega';
import { Card, KpiCard } from '../../components/ui';

export function EnterpriseGAPage() {
  const dashboard = buildGADashboard();

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{LEP_EG_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Enterprise GA Platform</h1>
        <p className="mt-1 text-sm text-slate-500">
          General Availability, Commercial Launch, SaaS Operations, Customer Lifecycle & Continuous Innovation —
          operational foundation for running the AI Commerce Operating System as a world-class SaaS business.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(LIVE_ROUTES).map(([key, path]) => (
            <Link
              key={key}
              to={path}
              className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300"
            >
              {path}
            </Link>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <KpiCard label="Command Center Metrics" value={dashboard.command_center_metrics.toString()} />
        <KpiCard label="Commercial Capabilities" value={dashboard.commercial_capabilities.toString()} />
        <KpiCard label="Lifecycle Stages" value={dashboard.lifecycle_stages.toString()} />
        <KpiCard label="Revenue Metrics" value={dashboard.revenue_metrics.toString()} />
        <KpiCard label="Innovation Capabilities" value={dashboard.innovation_capabilities.toString()} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">Architecture Pipeline</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ARCHITECTURE_STAGES.map((s, i) => (
            <span key={s} className="flex items-center gap-1 text-xs">
              <span className="rounded-lg border border-slate-200 px-2 py-1 dark:border-slate-700">{s}</span>
              {i < ARCHITECTURE_STAGES.length - 1 && <span className="text-slate-400">→</span>}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">GA Command Center</h2>
        <p className="mt-1 text-sm text-slate-500">{COMMAND_CENTER_METRICS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <GACommandCenterPanel kpis={DEMO_KPIS} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Commercial Operations</h2>
          <p className="mt-1 text-sm text-slate-500">{COMMERCIAL_OPS.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <CommercialOperationsPanel items={DEMO_COMMERCIAL} />
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Customer Lifecycle</h2>
          <p className="mt-1 text-sm text-slate-500">{LIFECYCLE_STAGES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <CustomerLifecyclePanel funnel={DEMO_LIFECYCLE} />
          </Card>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Revenue Operations</h2>
        <p className="mt-1 text-sm text-slate-500">{REVENUE_METRICS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <RevenueOperationsPanel metrics={DEMO_REVENUE} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Customer Success Operations</h2>
        <p className="mt-1 text-sm text-slate-500">{CUSTOMER_SUCCESS_OPS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <CustomerSuccessOpsPanel items={DEMO_CUSTOMER_SUCCESS} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Product & Release Operations</h2>
        <p className="mt-1 text-sm text-slate-500">{PRODUCT_OPS.join(' · ')} · {RELEASE_OPS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <ProductReleaseOpsPanel items={DEMO_PRODUCT_RELEASE} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Marketplace & AI Operations</h2>
        <p className="mt-1 text-sm text-slate-500">{MARKETPLACE_OPS.join(' · ')} · {AI_OPS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <MarketplaceAIOpsPanel items={DEMO_MARKETPLACE_AI} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Innovation Platform</h2>
        <p className="mt-1 text-sm text-slate-500">
          {PARTNER_TYPES.join(' · ')} · {EXTENSION_TYPES.join(' · ')} · {ROADMAP_CAPABILITIES.join(' · ')} · {INNOVATION_CAPABILITIES.join(' · ')}
        </p>
        <Card className="mt-4" padding="sm">
          <InnovationPlatformPanel items={DEMO_INNOVATION} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <div>
          <h2 className="text-lg font-semibold">Support</h2>
          <p className="mt-2 text-xs text-slate-500">{SUPPORT_CHANNELS.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Analytics</h2>
          <p className="mt-2 text-xs text-slate-500">{GA_ANALYTICS.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Governance</h2>
          <p className="mt-2 text-xs text-slate-500">{GOVERNANCE_AREAS.join(' · ')}</p>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Accessibility</h2>
        <div className="mt-2 flex flex-wrap gap-1">
          {ACCESSIBILITY_STANDARDS.map((s) => (
            <span key={s} className="rounded border border-slate-200 px-2 py-0.5 text-xs dark:border-slate-700">{s}</span>
          ))}
        </div>
      </section>
    </div>
  );
}
